<script lang="ts" setup>
import Button from '@/components/Button.vue'
import Modal from '@/components/Modal.vue'
import Ticker from '@/components/Ticker.vue'
import { computed, ref } from 'vue'
import { subscribe } from '@/services/klaviyo'
import Message from '@/components/Message.vue'
import IconStatusError from '@/components/icons/IconStatusError.vue'
import formatMoney from '@/lib/formatters/money'

let mixpanelId: string | undefined
if (window.mixpanel && window.mixpanel.get_distinct_id) {
  mixpanelId = window.mixpanel.get_distinct_id()
}

const discount = ref<string | null>(null)
const klaviyoListId = ref<string | null>(null)
const klaviyoSource = ref<string | null>(null)

const upsellProduct = ref<{
  variantId: string
  ticker: string
  title: string
  price: string
  compareAtPrice: string
  copy: string
  cta: string
  cancelCta: string
  imageSrc: string
} | null>(null)

const emailAddressRef = ref('')
const showDiscount = ref(false)
const open = ref(false)

const busy = ref(false)
const error = ref<string | null>(null)

const dataEl = document.querySelector('[data-config-landing-page-product]')
const productVariantId = dataEl?.getAttribute(
  'data-config-landing-page-product',
)
if (dataEl?.getAttribute('data-upsell-product')) {
  upsellProduct.value = {
    variantId: dataEl.getAttribute('data-upsell-product') as string,
    ticker: dataEl.getAttribute('data-upsell-ticker') as string,
    title: dataEl.getAttribute('data-upsell-title') as string,
    price: dataEl.getAttribute('data-upsell-price') as string,
    compareAtPrice: dataEl.getAttribute(
      'data-upsell-compare-at-price',
    ) as string,
    copy: dataEl.getAttribute('data-upsell-copy') as string,
    cta: dataEl.getAttribute('data-upsell-cta') as string,
    cancelCta: dataEl.getAttribute('data-upsell-cancel-cta') as string,
    imageSrc: dataEl.getAttribute('data-upsell-image-src') as string,
  }
}
if (dataEl && productVariantId) {
  klaviyoListId.value = dataEl.getAttribute('data-klaviyo-list-id')
  klaviyoSource.value = dataEl.getAttribute('data-klaviyo-source')
  discount.value = dataEl.getAttribute('data-discount')

  document.querySelectorAll('[data-email-capture-app-id]').forEach((el) => {
    if (el.getAttribute('data-email-capture-app-id') === productVariantId) {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        open.value = true
        window.mixpanel.track('landing_page_cta_click', {
          landingPageProductId: productVariantId,
        })
      })
    }
  })

  // uncomment for testing
  // open.value = true
  // showDiscount.value = true
}

const submit = async () => {
  error.value = null
  busy.value = true
  const email = emailAddressRef.value.trim()
  if (email && /@/.test(email)) {
    try {
      const res = await subscribe({
        email: emailAddressRef.value,
        list_id: klaviyoListId.value as string,
        custom_source: klaviyoSource.value as string,
        properties: {
          $consent: ['email'],
        },
      })
      if (res.status === 202) {
        // show discount
        showDiscount.value = true
        window.mixpanel.track('landing_page_email_submit_success', {
          upsellProductId: upsellProduct.value?.variantId,
        })
      } else {
        throw new Error('Bad')
      }
    } catch (e) {
      // todo handle error
      error.value =
        'Sorry, something went wrong. If you need any help, please get in touch with us at hello@bowercollective.com'
    }
    busy.value = false
  } else {
    error.value = 'Please enter a valid email address'
    busy.value = false
  }
}

const onClickCheckoutBoth = () => {
  window.mixpanel.track('landing_page_checkout_click', {
    checkoutButton: 'upsell_both',
  })
}

const onClickCheckoutSingle = () => {
  window.mixpanel.track('landing_page_checkout_click', {
    checkoutButton: 'upsell_single',
  })
}

const onClickCheckout = () => {
  window.mixpanel.track('landing_page_checkout_click', {
    checkoutButton: 'checkout',
  })
}

const qs = computed(() => {
  const params = new URLSearchParams()
  if (discount.value) params.set('discount', discount.value)
  if (mixpanelId) params.set('attributes[mixpanel_id]', mixpanelId)
  return params.toString()
})
</script>

<template>
  <Modal
    v-if="open && !showDiscount"
    title="Get your discount code to claim your offer"
    @close="open = false"
  >
    <form class="DiscountEmailCapture__Form" @submit.prevent>
      <label>
        <span>Email address</span>
        <input type="text" v-model="emailAddressRef" name="email" />
      </label>
      <div class="Stack Stack--tiny">
        <Message mode="error" v-if="error">
          <template #icon><IconStatusError></IconStatusError></template>
          <template #content>{{ error }}</template>
        </Message>
        <Button
          :disabled="emailAddressRef.length === 0 && !busy"
          :modifiers="['block']"
          @click="submit"
        >
          Reveal your code
        </Button>
      </div>
      <p class="DiscountEmailCapture__Form__Disclaimer">
        By providing your email you're agreeing to receive the latest news,
        exciting new product announcements and our best offers. You can
        unsubscribe at any time by following the link in your latest email.
      </p>
    </form>
  </Modal>
  <Modal
    @close="open = false"
    v-else-if="open && showDiscount"
    title="Thanks! Here’s your code"
  >
    <div>
      <p class="DiscountEmailCapture__Code">{{ discount }}</p>
      <p>
        Use this code to claim your offer. It will automatically apply when you
        checkout.
      </p>
      <Button
        :modifiers="['block']"
        :href="`/cart/${productVariantId}:1?${qs}`"
        @click="onClickCheckout"
        >Checkout now</Button
      >
    </div>
    <div v-if="upsellProduct" class="DiscountEmailCapture__Upsell">
      <Ticker :message="upsellProduct.ticker"></Ticker>
      <h4 class="DiscountEmailCapture__Upsell__Title">
        {{ upsellProduct.title }}
      </h4>
      <p>
        <span class="ProductCard__Price ProductCard__Price--sale-price h4">{{
          upsellProduct.price
        }}</span>
        <span class="ProductCard__Price ProductCard__Price--old-price h4">{{
          formatMoney(upsellProduct.compareAtPrice)
        }}</span>
      </p>
      <div class="Stack">
        <img :src="upsellProduct.imageSrc" />
        <div v-html="upsellProduct.copy"></div>
        <div class="Stack Stack--micro">
          <Button
            :href="`/cart/${productVariantId}:1,${upsellProduct.variantId}:1?${qs}`"
            :modifiers="['block']"
            @click="onClickCheckoutBoth"
            >{{ upsellProduct.cta }}</Button
          >
          <Button
            :href="`/cart/${productVariantId}:1?${qs}`"
            :modifiers="['block', 'inverted']"
            @click="onClickCheckoutSingle"
            >{{ upsellProduct.cancelCta }}</Button
          >
        </div>
      </div>
    </div>
  </Modal>
</template>

<style>
.DiscountEmailCapture__Form {
  padding: var(--grid-gap) 0 0 0;

  & label {
    display: block;
    margin-bottom: var(--grid-gap);

    & span {
      font-weight: bold;
      display: block;
      margin-bottom: 5px;
    }

    & input[type='text'] {
      display: block;
      width: 100%;
      border: 1px solid #d3d8d7;
      border-radius: 6px;
      background: var(--color-white);
      padding: 10px;

      &:focus {
        border-color: var(--color-forest-80);
      }
    }
  }
}

.DiscountEmailCapture__Form__Disclaimer {
  color: var(--color-forest-80);
  font-size: 14px;
  margin-top: 10px;
}

.DiscountEmailCapture__Code {
  background-color: var(--color-forest-100);
  border-radius: var(--radius-large);
  padding: 28px 16px;
  color: var(--color-white);
  font-family: Courier, monospace;
  text-align: center;
  font-size: var(--font-size--h4);
  line-height: 1;

  &:first-child {
    padding: 28px 16px;
  }
}

.DiscountEmailCapture__Upsell .Ticker {
  margin: 12px -24px;
}

.DiscountEmailCapture__Upsell__Title {
  margin-top: 0;
}

.DiscountEmailCapture__Upsell__Subtitle {
  margin-top: 0;
}
</style>
